import React, { useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { HeaderOptionsMenu } from './HeaderOptionsMenu';
import { CatalogueModal } from './catalog';
import getInitials from 'src/utils/getInitials';
import StorefrontIcon from '@material-ui/icons/Storefront';
import { ThreadTagsLabel } from '../Tags';
import { CallButton } from './CallButton';
import { formatDisplayPhone } from 'src/modules/phones/utils/formatDisplayPhone';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { Switch } from '../ChatBot';
import { useWarboxConversation } from 'src/hooks';
import { IConversation } from 'src/interfaces/conversation';

export const Header = ({
    name,
    phone,
    normalizedPhone,
    contactCountryCode,
    image,
    tags,
    linkedContactId,
    conversationStatus,
    storeId,
    area,
    senderPhone,
    callerPhone,
    storeCountryCode,
    canSendMessages,
    isSilenced,
    onClick,
    onOpenDetails,
    onOpenNotes,
    onOpenCallHistory,
    onConversationStatusUpdated,
    onCloseChat,
    conversation,
}: {
    name: string;
    phone: string;
    normalizedPhone?: string;
    contactCountryCode?: ECountryCode;
    image?: string;
    tags?: any[];
    linkedContactId?: string;
    conversationStatus?: string;
    storeId?: string;
    area?: string;
    senderPhone?: string;
    callerPhone?: string;
    storeCountryCode?: string;
    canSendMessages?: boolean;
    isSilenced?: boolean;
    onClick?: () => void;
    onOpenDetails?: () => void;
    onOpenNotes?: () => void;
    onOpenCallHistory?: () => void;
    onConversationStatusUpdated?: (status: string) => void;
    onCloseChat?: () => void;
    conversation?: IConversation;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [openCatalogue, setOpenCatalogue] = useState(false);
    const { updateConversation } = useWarboxConversation();
    const formattedPhone = useMemo(
        () =>
            formatDisplayPhone(normalizedPhone || phone, {
                countryCode: contactCountryCode,
                fallbackCountryCode:
                    (storeCountryCode as ECountryCode) || ECountryCode.Mexico,
            }),
        [normalizedPhone, phone, contactCountryCode, storeCountryCode]
    );

    const isBlocked = conversation?.status === 'blocked';
    const isChannelEnabled = canSendMessages !== false;

    const isChatBotActive = conversation?.isUsingChatBot || false;

    const handleToggleChatBot = async () => {
        if (isBlocked || !isChannelEnabled || !conversation?._id) {
            return;
        }

        await updateConversation(
            { isChatBotActive: !isChatBotActive },
            conversation._id
        );
    };

    return (
        <Box
            p={2}
            borderBottom={`1px solid ${theme.palette.divider}`}
            bgcolor={theme.palette.background.paper}
            display="flex"
            alignItems="center"
        >
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    gap: 15,
                    cursor: 'pointer',
                }}
                onClick={onClick}
            >
                <Avatar src={image} style={{ width: 64, height: 64 }}>
                    {getInitials(name)}
                </Avatar>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 50,
                        }}
                    >
                        <Typography
                            variant="h3"
                            color="textPrimary"
                            style={{
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {name}
                        </Typography>
                        <ThreadTagsLabel tags={tags || []} size="lg" />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                        {formattedPhone}
                    </Typography>
                </Box>
            </Box>
            <Switch checked={isChatBotActive} onChange={handleToggleChatBot} />

            <Box style={{ display: 'flex', gap: 5 }}>
                <Tooltip title={t('Chat.OpenCatalog')}>
                    <IconButton
                        color="primary"
                        onClick={() => setOpenCatalogue(true)}
                    >
                        <StorefrontIcon />
                    </IconButton>
                </Tooltip>

                <CatalogueModal
                    open={openCatalogue}
                    setOpen={setOpenCatalogue}
                    conversationId={conversation?._id}
                    storeId={storeId}
                    area={area}
                    recipientPhone={phone}
                    senderPhone={senderPhone}
                    isBlocked={conversationStatus === 'blocked'}
                    canSendMessages={canSendMessages}
                />

                <CallButton
                    conversationId={conversation?._id}
                    contactId={linkedContactId}
                    name={name}
                    phone={phone}
                    storeId={storeId}
                    area={area}
                    callerPhone={callerPhone}
                    storeCountryCode={storeCountryCode}
                    onOpenCallHistory={onOpenCallHistory}
                />

                <HeaderOptionsMenu
                    conversationId={conversation?._id}
                    conversationStatus={conversationStatus}
                    storeId={storeId}
                    currentTags={tags}
                    isSilenced={isSilenced}
                    onOpenDetails={onOpenDetails}
                    onOpenNotes={onOpenNotes}
                    onConversationStatusUpdated={onConversationStatusUpdated}
                    onCloseChat={onCloseChat}
                />
            </Box>
        </Box>
    );
};
